import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Tour } from "./types";

/**
 * Tour state lives at app-root via {@link TourProvider}. Every page calls
 * {@link useAutoStartTour} on mount to register its tour and auto-start
 * once per user-browser, gated by localStorage. The header's
 * {@link TourLauncher} reads the registry to offer manual restart.
 */
interface TourContextValue {
  /** Currently active tour, or null when no tour is running. */
  active: Tour | null;
  /** Step index within the active tour (0-based). */
  stepIndex: number;
  /** Tours registered by mounted pages — keyed by Tour.id. */
  registry: Record<string, Tour>;

  start: (tour: Tour, opts?: { force?: boolean }) => void;
  next: () => void;
  prev: () => void;
  /** Stops the tour and writes "dismissed" to localStorage for that id. */
  stop: () => void;
  /** Stops without persisting dismissal — for "X" close vs explicit "Done". */
  cancel: () => void;
  register: (tour: Tour) => void;
  unregister: (id: string) => void;
  /** True when the user has dismissed this tour id at least once. */
  isDismissed: (id: string) => boolean;
  /** Erases all dismissals (used by a "Reset tours" admin button). */
  resetAllDismissals: () => void;
}

const Ctx = createContext<TourContextValue | null>(null);

const STORAGE_PREFIX = "datumcraft.tour.dismissed.";

function readDismissed(id: string): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + id) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(id: string, value: boolean) {
  try {
    if (value) localStorage.setItem(STORAGE_PREFIX + id, "1");
    else localStorage.removeItem(STORAGE_PREFIX + id);
  } catch {
    /* localStorage may be blocked (Safari private mode); fail open */
  }
}

export function TourProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<Tour | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [registry, setRegistry] = useState<Record<string, Tour>>({});

  // Ref mirror of `active` so the start() callback can read the
  // current value without re-creating itself on every active change.
  // Needed for the cross-page guard below.
  const activeRef = useRef<Tour | null>(null);
  activeRef.current = active;

  const start = useCallback<TourContextValue["start"]>((tour, opts) => {
    if (!opts?.force) {
      // Auto-start path (force=false): skip if the user already
      // dismissed this tour, AND skip if a tour is already running.
      // The second guard is what makes cross-page flows survive a
      // route change — when the overlay navigates /chat → /saved
      // mid-flow, SavedQuestionsPage's useAutoStartTour fires
      // start(savedTour); without this guard that would clobber the
      // in-progress cross-page tour. force=true (launcher / hub)
      // always wins.
      if (readDismissed(tour.id)) return;
      if (activeRef.current) return;
    }
    setActive(tour);
    setStepIndex(0);
  }, []);

  const stop = useCallback(() => {
    setActive((current) => {
      if (current) writeDismissed(current.id, true);
      return null;
    });
    setStepIndex(0);
  }, []);

  const cancel = useCallback(() => {
    setActive(null);
    setStepIndex(0);
  }, []);

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (!active) return i;
      const last = active.steps.length - 1;
      if (i >= last) {
        // Last step → finish + persist dismissal.
        writeDismissed(active.id, true);
        setActive(null);
        return 0;
      }
      return i + 1;
    });
  }, [active]);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const register = useCallback((tour: Tour) => {
    setRegistry((r) => (r[tour.id] === tour ? r : { ...r, [tour.id]: tour }));
  }, []);

  const unregister = useCallback((id: string) => {
    setRegistry((r) => {
      if (!(id in r)) return r;
      const next = { ...r };
      delete next[id];
      return next;
    });
  }, []);

  const isDismissed = useCallback((id: string) => readDismissed(id), []);

  const resetAllDismissals = useCallback(() => {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX)) keys.push(k);
      }
      keys.forEach((k) => localStorage.removeItem(k));
    } catch {
      /* fail open */
    }
  }, []);

  // Esc closes the active tour without persisting (matches "cancel").
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancel();
      if (e.key === "ArrowRight" || e.key === "Enter") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, cancel, next, prev]);

  const value = useMemo<TourContextValue>(
    () => ({
      active,
      stepIndex,
      registry,
      start,
      next,
      prev,
      stop,
      cancel,
      register,
      unregister,
      isDismissed,
      resetAllDismissals,
    }),
    [active, stepIndex, registry, start, next, prev, stop, cancel, register, unregister, isDismissed, resetAllDismissals],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTour(): TourContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTour must be used within <TourProvider>");
  return v;
}

/**
 * Pages call this on mount to register their tour AND auto-start it
 * the first time the user lands on the page. Subsequent visits are
 * silent (the tour is in localStorage as dismissed); the user can
 * always replay via the header launcher.
 */
export function useAutoStartTour(tour: Tour) {
  const { register, unregister, start } = useTour();
  useEffect(() => {
    register(tour);
    // Auto-start ONLY if not previously dismissed. force=false (default).
    start(tour);
    return () => unregister(tour.id);
    // We intentionally key on tour.id only — re-running on every render
    // would re-start the tour over and over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.id]);
}
